import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { CASE_STUDIES, getNextClient } from "../data/caseStudies";
import { CaseBlock, MediaItem, BlockLabel, reveal } from "../components/casestudy/CaseStudyBlocks";
import { NextProject } from "../components/casestudy/NextProject";
import { ProjectModal } from "../components/casestudy/ProjectModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fallbackCase = (client) => ({
  tagline: client.description,
  role: (client.services || []).join(" / "),
  year: "2024–2026",
  intro: client.description,
  hero: { title: "HERO VIDEO", media: { ph: { kind: "video", format: "HERO VIDEO / 16:9", dims: "1920 × 1080", aspect: "16:9" } } },
  blocks: (client.projects || []).map((p) => ({ type: "full", title: p.title.toUpperCase(), media: { projectId: p.id } })),
});

export default function ClientPage() {
  const { slug } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    const fetchClient = async () => {
      try { const res = await axios.get(`${API}/clients/${slug}`); setClient(res.data); }
      catch { toast.error("Failed to load client page"); }
      finally { setLoading(false); }
    };
    fetchClient();
  }, [slug]);

  if (loading) return <div className="min-h-screen pt-20 bg-[#F8F6F3] flex items-center justify-center"><div className="w-8 h-8 border-4 border-[#FF5E3A] border-t-transparent rounded-full animate-spin" /></div>;
  if (!client) return (
    <div className="min-h-screen pt-20 bg-[#F8F6F3] flex flex-col items-center justify-center gap-6">
      <h1 className="text-4xl font-bold text-[#1C1C1C]">Client not found</h1>
      <Link to="/portfolio"><button className="bg-[#FF5E3A] text-white font-semibold text-sm px-6 py-3 rounded-full"><ArrowLeft size={14} className="inline mr-2" />Back to Portfolio</button></Link>
    </div>
  );

  const cs = CASE_STUDIES[slug] || fallbackCase(client);
  const projectMap = Object.fromEntries((client.projects || []).map((p) => [p.id, p]));
  const next = getNextClient(slug);
  let mediaNum = 1;

  return (
    <div className="min-h-screen pt-20 bg-[#F8F6F3]" data-testid="client-page">
      {/* Hero */}
      <section className="pt-16 md:pt-24 pb-10 md:pb-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <Link to="/portfolio" className="inline-flex items-center gap-2 text-sm font-medium text-[#FF5E3A] mb-10 md:mb-14 hover:text-[#1C1C1C] transition-colors" data-testid="back-to-portfolio">
              <ArrowLeft size={16} /> All Work
            </Link>
            {client.logo && <img src={client.logo} alt={`${client.name} logo`} className="h-10 md:h-12 object-contain mb-6 md:mb-8" />}
            <h1 className="text-5xl sm:text-6xl md:text-8xl font-black tracking-tighter text-[#1C1C1C] leading-[0.95] mb-6 md:mb-8" data-testid="client-title">{client.name}</h1>
            <p className="text-xl md:text-2xl leading-snug text-[#1C1C1C]/55 max-w-3xl font-medium">{cs.tagline}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.2 }} className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-10 border-t border-[#1C1C1C]/10 mt-10 md:mt-14 pt-8 md:pt-10">
            {[["Role", cs.role], ["Client", client.name], ["Year", cs.year]].map(([label, value]) => (
              <div key={label}>
                <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.25em] text-[#FF5E3A] mb-2">{label}</p>
                <p className="text-sm md:text-base font-semibold text-[#1C1C1C]/75 leading-snug">{value}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Hero media */}
      <section className="pb-8 md:pb-12">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}>
            <BlockLabel num="01" title={cs.hero.title} />
            <MediaItem item={cs.hero.media} projectMap={projectMap} onOpen={setSelectedProject} />
          </motion.div>
        </div>
      </section>

      {/* The project */}
      <section className="py-16 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div {...reveal} className="max-w-4xl">
            <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.25em] text-[#FF5E3A] mb-5 md:mb-7">The Project</p>
            <p className="text-xl md:text-3xl leading-snug font-medium text-[#1C1C1C]/80" data-testid="client-intro">{cs.intro}</p>
            <Link to="/contact" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1C1C1C] mt-8 group/cta hover:text-[#FF5E3A] transition-colors" data-testid="client-contact-link">
              Have a similar project? Start a conversation
              <ArrowRight size={15} className="transition-transform duration-300 group-hover/cta:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Case study blocks */}
      <section className="pb-24 md:pb-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {cs.blocks.map((block, i) => {
            if (block.type !== "text") mediaNum += 1;
            const num = String(mediaNum).padStart(2, "0");
            return <CaseBlock key={i} block={block} num={num} projectMap={projectMap} onOpen={setSelectedProject} />;
          })}
        </div>
      </section>

      <NextProject next={next} />

      <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
    </div>
  );
}
